import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { viewerZIndices } from '../../../viewer/viewer-z-indices';
import { useSubscribe } from '../../hooks/use-subscribe';
import { ProjectSpatialReferenceContext } from '../../state/project-reference-state';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import { Tooltip } from '../tooltip/tooltip';

const Bar = styled(Stack)`
  pointer-events: auto;
  z-index: ${viewerZIndices.menu};
  grid-column: 1 / -1;
  min-height: 22px;
  font-size: 12px;
  line-height: 1;
  color: ${(props) => props.theme.color.gray.dark};
  background-color: ${(props) => props.theme.color.gray.lightest};
  border-top: 1px solid ${(props) => props.theme.color.gray.light};
  font-variant-numeric: tabular-nums;
`;

const Item = styled(Stack).attrs({
  inline: true,
  direction: 'row',
  alignItems: 'center',
  spacing: '4px',
})``;

const IconWrapper = styled(Stack).attrs({
  inline: true,
  direction: 'row',
  alignItems: 'center',
})`
  color: ${(props) => props.theme.color.gray.darkest};
`;

const Label = styled.span`
  color: ${(props) => props.theme.color.gray.darkest};
`;

const PositionGroup = styled(Stack).attrs({
  inline: true,
  direction: 'row',
  alignItems: 'center',
  spacing: '0.5rem',
})``;

const formatCoord = (n: number) => n.toFixed(2);

const isPositioningTool = (name: string | undefined): boolean =>
  name === 'measure' || name === 'design' || name === 'poi';

const ViewerBottomInfoBar = () => {
  const { t } = useTranslation();
  const { coordinateSystem, geoid, getCoordinateSystemOrDefault } = React.useContext(
    ProjectSpatialReferenceContext,
  );
  const currentTool = useSubscribe(SubscriptionTopic.SetTool);
  const cursorPosition = useSubscribe(SubscriptionTopic.ViewerCursorPositionChanged);

  const projection = coordinateSystem ?? getCoordinateSystemOrDefault();
  const projectionName = projection?.name ?? '—';
  const geoidName = geoid?.name ?? '—';
  const isPositioningToolActive = isPositioningTool(currentTool?.name ?? '');
  const hasCursorPosition =
    cursorPosition != null &&
    typeof cursorPosition === 'object' &&
    'easting' in cursorPosition &&
    'northing' in cursorPosition &&
    'height' in cursorPosition;

  const projectionTooltip = t('geodataPage.details.projectionTitle', { ns: 'cloudProcessing' });
  const geoidTooltip = t('geodataPage.details.geoid', { ns: 'cloudProcessing' });
  // eslint-disable-next-line local-rules/valid-react-i18next-t -- bottomBar.positionTooltip in skyview.yaml
  const positionTooltip = t('bottomBar.positionTooltip', { ns: 'skyview' });

  return (
    <Bar
      alignItems="center"
      aria-label="Project spatial reference and cursor position"
      direction="row"
      height="22px"
      justifyContent="flex-start"
      padding="0 12px"
      role="status"
      spacing="1.5rem"
    >
      <Item>
        <Tooltip content={projectionTooltip} wrapChildren={true}>
          <IconWrapper>
            <Icon fixedWidth={true} icon={['fal', 'globe']} />
            <span>{projectionName}</span>
          </IconWrapper>
        </Tooltip>
      </Item>
      <Item>
        <Tooltip content={geoidTooltip} wrapChildren={true}>
          <IconWrapper>
            <Icon fixedWidth={true} icon={['fal', 'mountain-sun']} />
            <span>{geoidName}</span>
          </IconWrapper>
        </Tooltip>
      </Item>
      {isPositioningToolActive && (
        <Item>
          <Tooltip content={positionTooltip} wrapChildren={true}>
            <PositionGroup>
              <IconWrapper>
                <Icon fixedWidth={true} icon={['fal', 'crosshairs']} />
              </IconWrapper>
              <Item>
                <Label>N</Label>
                <span>{hasCursorPosition ? formatCoord(cursorPosition.northing) : '—'}</span>
              </Item>
              <Item>
                <Label>E</Label>
                <span>{hasCursorPosition ? formatCoord(cursorPosition.easting) : '—'}</span>
              </Item>
              <Item>
                <Label>H</Label>
                <span>{hasCursorPosition ? formatCoord(cursorPosition.height) : '—'}</span>
              </Item>
            </PositionGroup>
          </Tooltip>
        </Item>
      )}
    </Bar>
  );
};

export { ViewerBottomInfoBar };
